import { useEffect, useState, type ReactNode } from "react";
import { Icono, type NombreIcono } from "../../components/Icono";
import { Tarjeta } from "../../components/Tarjeta";

export type Severidad = "info" | "advertencia" | "error";

// Único mapa severidad → clases Tailwind para TODO lo relacionado a
// alertas del Dashboard (Franja de Estado) — se centraliza acá para que
// "error" siempre sea rojo, "advertencia" siempre ámbar y "info" siempre
// azul en cualquier pantalla, sin un color hardcodeado nuevo por archivo.
export const ESTILO_SEVERIDAD: Record<Severidad, string> = {
  info: "bg-info/10 border-l-info text-info",
  advertencia: "bg-advertencia/10 border-l-advertencia text-advertencia",
  error: "bg-error/10 border-l-error text-error",
};

export interface FilaMoneda {
  moneda: string;
  total: number;
}

export function formatearPorMoneda(filas: FilaMoneda[] | null): string {
  if (!filas || filas.length === 0) return "Sin movimientos";
  return filas.map((f) => `${f.moneda} ${f.total.toLocaleString("es-AR")}`).join(" · ");
}

// Agrupa las tarjetas de un dashboard en zonas identificables de un
// vistazo, sin tocar la lógica de datos — compartido entre los 3
// Dashboards (CEO/Ejecutivo/Judicial), Centro de Operaciones y Mi Trabajo.
// Más espacio entre secciones y un título algo más presente, sin perder
// la jerarquía (los números de las tarjetas siguen siendo lo más grande
// de la pantalla).
export function SeccionDashboard({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="mt-14 first:mt-0">
      <h2 className="mb-5 text-sm font-semibold uppercase tracking-wider text-texto">{titulo}</h2>
      {children}
    </section>
  );
}

// Envoltorio compartido para tarjetas de gráfico/lista: título consistente
// (antes se repetía "text-sm font-semibold" suelto en cada archivo, ahora
// es un solo punto de estilo) con más aire alrededor.
export function TarjetaGrafico({
  titulo,
  icono,
  className = "",
  children,
}: {
  titulo: string;
  icono?: NombreIcono;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Tarjeta interactiva tamano="grande" className={className}>
      <div className="mb-5 flex items-center gap-2.5">
        {icono && <Icono nombre={icono} className="h-5 w-5 text-texto-secundario" />}
        <h3 className="text-base font-semibold text-texto">{titulo}</h3>
      </div>
      {children}
    </Tarjeta>
  );
}

// Fade-in sutil al montar — usado tanto en el número animado como en los
// valores que ya vienen formateados como texto (montos), que no se
// re-formatean cuadro a cuadro para no reimplementar el formateo de
// moneda "en vivo". Exportado para que Resumen del día y Alerta del día
// reutilicen el mismo efecto sin duplicarlo.
export function useDesvanecido(): boolean {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return visible;
}

export type TonoKPI = "info" | "exito" | "alerta" | "advertencia" | "finanzas" | "neutro";

const ESTILO_TONO: Record<TonoKPI, string> = {
  info: "bg-info/10 text-info",
  exito: "bg-exito/10 text-exito",
  alerta: "bg-error/10 text-error",
  advertencia: "bg-advertencia/10 text-advertencia",
  finanzas: "bg-finanzas/10 text-finanzas",
  neutro: "bg-texto-secundario/10 text-texto-secundario",
};

// KPI reutilizable: ícono grande con color propio (tono), número como
// elemento dominante de la tarjeta, y una fila de tendencia siempre
// presente — por defecto un placeholder ("Sin comparación", sin inventar
// ningún cálculo), reemplazable vía `tendencia` cuando la pantalla sí tiene
// una comparación real ya calculada por el backend (ver Vista General:
// "Facturación del mes vs. mes anterior"). Compartido entre los 3
// Dashboards. `destacado` la convierte en la tarjeta "Hero" de la pantalla
// — mismo dato, mucho más protagonismo visual (no cambia qué se calcula,
// sólo cómo se muestra).
//
// El valor se muestra DIRECTO, sin animación de conteo: la versión anterior
// animaba de 0 al valor final con requestAnimationFrame, y si el navegador
// no llegaba a despachar el frame final (pestaña en background, tab
// inactiva, etc.) el número se quedaba pegado en 0 indefinidamente aunque
// el resto de la pantalla ya mostrara el dato real — causa raíz confirmada
// del bug de KPIs en "0". El fade-in de opacidad (useDesvanecido) es
// puramente CSS y no puede dejar un número incorrecto en pantalla.
export function TarjetaKPI({
  etiqueta,
  valor,
  subtitulo,
  tendencia,
  icono,
  tono = "neutro",
  destacado = false,
  className = "",
}: {
  etiqueta: string;
  valor: string | number;
  subtitulo?: string;
  /** Reemplaza el placeholder "Sin comparación" cuando hay una comparación real ya calculada. */
  tendencia?: string;
  icono: NombreIcono;
  tono?: TonoKPI;
  destacado?: boolean;
  className?: string;
}) {
  const esNumero = typeof valor === "number";
  const visible = useDesvanecido();

  return (
    <Tarjeta tamano="grande" interactiva className={`flex flex-col justify-between ${className}`}>
      <div className="flex items-start justify-between gap-3">
        <p
          className={`font-semibold uppercase tracking-wider text-texto-secundario ${destacado ? "text-sm" : "text-xs"}`}
        >
          {etiqueta}
        </p>
        <span
          className={`flex flex-shrink-0 items-center justify-center rounded-full ${ESTILO_TONO[tono]} ${
            destacado ? "h-12 w-12" : "h-9 w-9"
          }`}
        >
          <Icono nombre={icono} className={destacado ? "h-6 w-6" : "h-5 w-5"} />
        </span>
      </div>
      <div className={`transition-opacity duration-500 ${visible ? "opacity-100" : "opacity-0"} ${destacado ? "mt-5" : "mt-3"}`}>
        <p className={`font-semibold tracking-tight text-texto ${destacado ? "text-4xl" : "text-2xl"}`}>
          {esNumero ? (valor as number).toLocaleString("es-AR") : valor}
        </p>
        {subtitulo && <p className="mt-2 text-sm text-texto-secundario">{subtitulo}</p>}
        <p className="mt-3 border-t border-borde pt-2.5 text-xs text-texto-secundario/70">{tendencia ?? "Sin comparación"}</p>
      </div>
    </Tarjeta>
  );
}
